import React, { useEffect, useState, useRef } from "react";
import Header from "../../components/header/Header";
import MuiAppBar from "@mui/material/AppBar";
import SideMenu from "../../components/sidemenu/SideMenu";
import { Box, CircularProgress, Drawer, styled } from "@mui/material";
import { toast } from "react-toastify";
import { useDispatch, useSelector } from "react-redux";
import { getOrder, updateOrderStatus } from "../../store/reducers/orders";
import { useTranslation } from "react-i18next";
import {
  registerDeviceToken,
  getAllNotification,
  readAllNotification,
} from "../../store/reducers/notification";
import NewCheckInReqModal from "../../components/NotificationModal/NewCheckInReqModal";
import { useAbly } from "../../components/app/AblyWrapper";

const drawerWidth = 24;
//change this to 24rem in drawer and appbar
const AppBar = styled(MuiAppBar)({
  width: `calc(100% - ${drawerWidth}rem)`,
  backgroundColor: "#fff",
  boxShadow: "none",
  marginLeft: "24rem",
});

const PageLayout = ({
  children,
  headerTitle,
  showBackBtn,
  showSearchField = true,
  handleChange,
  fetchDashboardData = () => {},
  statusConfig,
  order_id,
  fetchOrderData,
  showNotification = true,
}) => {
  const { loading } = useSelector(state => state.orders.getOrderApi);
  const [data, setData] = useState();
  const [currentUserType, setCurrentUserType] = useState(0);
  const [openNewCheckInModal, setOpenNewCheckInModal] = useState(false);
  const dispatch = useDispatch();
  const [AllNotification, setAllNotification] = useState();
  const { t } = useTranslation();
  const { isNotificationFetched } = useSelector(state => state.notification);
  const { event, publish, clearEvent } = useAbly();

  // Queue system for multiple popups
  const [notificationQueue, setNotificationQueue] = useState([]);
  const [currentNotification, setCurrentNotification] = useState(null);
  const currentNotificationRef = useRef(null);

  const onCloseNewCheckIn = () => {
    setOpenNewCheckInModal(false);
    setCurrentNotification(null);
    currentNotificationRef.current = null;
  };

  const processNotification = async (notificationData) => {
    currentNotificationRef.current = notificationData;
    setCurrentNotification(notificationData);
    setData(notificationData);
    setOpenNewCheckInModal(true);
    
    // Set user type based on the data
    if (notificationData?.receiver === null) {
      setCurrentUserType(0);
    } else {
      setCurrentUserType(1);
    }
  };

  const addToQueue = (notificationData) => {
    // Deduplicate: don't queue the same order that's already showing
    if (currentNotificationRef.current?.id === notificationData?.id) return;

    setNotificationQueue(prev => {
      if (!currentNotificationRef.current && prev.length === 0) {
        processNotification(notificationData);
        return prev;
      }
      // Deduplicate: skip if same order already in queue
      if (prev.some(n => n?.id === notificationData?.id)) return prev;
      return [...prev, notificationData];
    });
  };

  const fetchAllNotification = () => {
    dispatch(getAllNotification()).then(res => {
      if (!res.error) {
        setAllNotification(res.payload);
      }
    });
  };

  const createToken = async playerId => {
    localStorage.setItem("onesignalPlayerId", playerId);
    dispatch(
      registerDeviceToken({
        registration_id: playerId,
        type: "web",
      }),
    );
  };

  useEffect(() => {
    if (isNotificationFetched === false) {
      fetchAllNotification();
    }
    localStorage.removeItem("firebaseToken");

    // Always init OneSignal and set up listeners on mount
    const setupOneSignal = async () => {
      if (typeof window === 'undefined') return;

      // Wait for SDK
      await new Promise((resolve) => {
        const check = setInterval(() => {
          if (window.OneSignal) { clearInterval(check); resolve(); }
        }, 300);
      });

      try {
        if (!window._oneSignalInitialized) {
          await window.OneSignal.init({ appId: "77fc6676-a5c3-4fcf-a0bf-30c81caf150e" });
          window._oneSignalInitialized = true;
          console.log("OneSignal initialized");
        }

        // Set up listeners only once
        if (!window._oneSignalListenersRegistered) {
          // Set up foreground listener
          const foregroundHandler = (event) => {
            const notif = event?.notification || {};
            const title = notif?.title || notif?.additionalData?.title || 'Notification';
            const body = notif?.body || notif?.additionalData?.body || '';
            toast.info(`${title}: ${body}`, {
              position: 'top-right', autoClose: 5000,
            });
          };

          // Set up click listener
          const clickHandler = (event) => {
            const notif = event?.notification || {};
            const data = notif?.additionalData || {};
            const id = data?.order_id || data?.id;
            if (id) fetchData(id).then(d => { if (d) addToQueue(d); });
          };

          window.OneSignal.Notifications.addEventListener('foregroundWillDisplay', foregroundHandler);
          window.OneSignal.Notifications.addEventListener('click', clickHandler);

          window._oneSignalListenersRegistered = true;
          window._oneSignalHandlers = { foregroundHandler, clickHandler };
          console.log("OneSignal listeners registered");
        }

        // Check if we already have a stored player ID
        const storedPlayerId = localStorage.getItem("onesignalPlayerId");

        if (!storedPlayerId) {
          // No stored token — request permission
          console.log("Requesting OneSignal permission");
          const permission = await window.OneSignal.Notifications.requestPermission();
          console.log("OneSignal permission:", permission);

          if (permission) {
            // Poll for player ID
            let playerId = null;
            for (let i = 0; i < 15; i++) {
              await new Promise(r => setTimeout(r, 1000));
              try { playerId = await window.OneSignal.User?.getOnesignalId?.(); } catch(e) {}
              if (!playerId) try { playerId = await window.OneSignal.getUserId?.(); } catch(e) {}
              if (!playerId) playerId = window.OneSignal.User?.PushSubscription?.id;
              if (playerId) break;
            }
            console.log("OneSignal web player ID:", playerId);
            if (playerId) createToken(playerId);
          }
        } else {
          // Already have token — re-register with backend to ensure it's current
          console.log("Re-registering existing token:", storedPlayerId);
          createToken(storedPlayerId);
        }
      } catch (err) {
        console.error("OneSignal setup error:", err);
      }
    };

    setupOneSignal();

    // Handle service worker messages (background notifications)
    const handleSwMessage = event => {
      if (event.data?.type === 'NOTIFICATION_CLICK' && event.data?.id) {
        fetchData(event.data.id).then(notificationData => {
          if (notificationData) addToQueue(notificationData);
        });
      }
    };
    navigator.serviceWorker?.addEventListener('message', handleSwMessage);

    return () => {
      navigator.serviceWorker?.removeEventListener('message', handleSwMessage);
    };
  }, []);

  const fetchData = async id => {
    return await dispatch(getOrder(id)).then(res => {
      if (!res.error) {
        return res?.payload;
      }
      return null;
    });
  };

  useEffect(() => {
    if (!event) return;
    
    console.log("Ably event received:", event);
    let payload = event?.data;
    
    const handleNotification = async () => {
      try {
        const notificationData = await fetchData(payload?.id);
        if (notificationData) {
          addToQueue(notificationData);
        }
        
        fetchDashboardData(statusConfig);
        fetchAllNotification();
        
        if (payload?.id === order_id) {
          fetchOrderData(order_id);
        }
      } catch (error) {
        console.error("Error handling notification:", error);
      } finally {
        clearEvent();
      }
    };
    
    handleNotification();
  }, [event]);

  // React-driven queue: process next item when currentNotification clears
  useEffect(() => {
    if (!currentNotification && notificationQueue.length > 0) {
      const next = notificationQueue[0];
      setNotificationQueue(prev => prev.slice(1));
      processNotification(next);
    }
  }, [currentNotification, notificationQueue]);

  useEffect(() => {
    if (data?.receiver === null) {
      setCurrentUserType(0);
    } else {
      setCurrentUserType(1);
    }
  }, [data]);

  const clickHandler = popupPayload => {
    const payloadForPickup = {
      pickup_status: popupPayload.order_status,
      pickup_comment: popupPayload.order_comment,
      is_message: popupPayload?.is_message ? true : false,
    };
    const payloadForReciever = {
      delivery_status: popupPayload.order_status,
      delivery_comment: popupPayload.order_comment,
      is_message: popupPayload?.is_message ? true : false,
    };
    const payload = {
      id: popupPayload.id,
      payload:
        currentUserType === 0
          ? { ...payloadForPickup }
          : { ...payloadForReciever },
    };
    
    const updateStatus = async () => {
      await dispatch(updateOrderStatus(payload))
        .then(res => {
          if (!res?.error) {
            toast.success(t("instructions_sent"));
          } else {
            toast.error(res?.error?.message || t("something_went_wrong"));
          }
        })
        .catch(err => {
          console.log(err);
        });
      
      onCloseNewCheckIn();
      fetchDashboardData(statusConfig);
      
      if (popupPayload.id === order_id) {
        fetchOrderData(order_id);
      }
    };
    
    updateStatus();
    dispatch(readAllNotification()).then(res => {
      if (!res.error) {
        fetchAllNotification();
      }
    });
  };

  return (
    <>
      <Box sx={{ display: "flex", minHeight: "100vh" }}>
        <AppBar position="fixed" open={true}>
          <Header
            headerTitle={headerTitle}
            showBackBtn={showBackBtn}
            showSearchField={showSearchField}
            handleChange={handleChange}
            AllNotification={AllNotification}
            showNotification={showNotification}
          />
        </AppBar>
        <Drawer
          sx={{
            width: `${drawerWidth}rem`,
            flexShrink: 0,
            "& .MuiDrawer-paper": {
              width: `${drawerWidth}rem`,
              boxSizing: "border-box",
            },
          }}
          variant="permanent"
          anchor="left"
          open={false}>
          <SideMenu />
        </Drawer>
        <Box
          component="main"
          sx={{
            marginTop: "3rem",
            flexGrow: 1,
          }}>
          {children}
        </Box>
      </Box>
      
      {loading ? (
        <CircularProgress sx={{ margin: "20rem 40rem" }} />
      ) : (
        <NewCheckInReqModal
          onClose={onCloseNewCheckIn}
          open={openNewCheckInModal}
          data={data}
          clickHandler={clickHandler}
          currentUserType={currentUserType}
        />
      )}
    </>
  );
};

export default PageLayout;